import React, {
    createContext,
    useContext,
    useMemo,
    useState,
    useCallback,
} from 'react'
import {
    AuthService,
    ImpersonationService,
    ImpersonationUtility,
} from 'utility'

const ImpersonationContext = createContext()

// Where the app lands after each transition (kept in one place so the flow is
// easy to audit).
const TARGET_LANDING = '/app/task'
const ADMIN_LANDING = '/app/settings/menu-permission?permissionTab=user'

// A hard reload is used on purpose after every token swap: this app has no
// central query cache to clear, so reloading is the only reliable way to drop
// every module-level cache and re-hydrate as the new identity.
const hardRedirect = path => {
    window.location.assign(path)
}

const readErrorCode = err => {
    const data = err?.error
    if (data && typeof data === 'object') {
        return data.ErrorCode || data.errorCode || null
    }
    return null
}

export const ImpersonationProvider = ({ children }) => {
    const [session, setSession] = useState(() =>
        ImpersonationUtility.getSession(),
    )
    const [starting, setStarting] = useState(false)
    const [ending, setEnding] = useState(false)

    const isImpersonating = !!session

    // Screen 2: modal submit. Returns { ok } on success (page then reloads) or
    // { ok:false, errorCode, status } so the modal can map the error.
    const startImpersonation = useCallback(async (target, reason) => {
        setStarting(true)
        try {
            // Stash the admin identity BEFORE swapping anything.
            ImpersonationUtility.stashAdmin()

            const res = await ImpersonationService.start(
                target.userId,
                reason,
            )

            const token = res?.Token || res?.token
            if (!token) {
                ImpersonationUtility.clearStash()
                return { ok: false, errorCode: 'TokenGenerateFailed' }
            }

            const targetUser = ImpersonationUtility.extractUser(res)
            const newSession = {
                sessionId: res?.SessionId || res?.sessionId || null,
                targetUserId: target.userId,
                targetName: targetUser?.FullName || target.name || target.email,
                targetEmail: targetUser?.Email || target.email,
                targetCompanyName:
                    targetUser?.CompanyName ||
                    targetUser?.Companies?.[0]?.CompanyName ||
                    target.companyName ||
                    '',
                actorName:
                    ImpersonationUtility.getStashedAdminUser()?.FullName || '',
                actorEmail:
                    ImpersonationUtility.getStashedAdminUser()?.Email || '',
                startedAtUtc: res?.StartedAtUtc || new Date().toISOString(),
                hardCapExpiresAtUtc:
                    res?.HardCapExpiresAtUtc || res?.hardCapExpiresAtUtc || null,
            }

            ImpersonationUtility.applyTarget(token, targetUser, newSession)
            hardRedirect(TARGET_LANDING)
            return { ok: true }
        } catch (err) {
            // Roll the stash back so a failed start never leaves us half-swapped.
            ImpersonationUtility.clearStash()
            return {
                ok: false,
                errorCode: readErrorCode(err),
                status: err?.status,
            }
        } finally {
            setStarting(false)
        }
    }, [])

    // Screen 3: banner "Return to admin".
    const endImpersonation = useCallback(async () => {
        setEnding(true)
        try {
            let freshAdminToken = null
            try {
                const res = await ImpersonationService.end()
                freshAdminToken = res?.AdminToken || res?.adminToken || null
            } catch {
                // Best effort: fall back to the stashed admin token below.
            }
            ImpersonationUtility.restoreAdmin(freshAdminToken)
            setSession(null)
            hardRedirect(ADMIN_LANDING)
        } finally {
            setEnding(false)
        }
    }, [])

    // Screen 4: idle/hard-cap expiry (countdown hit 0 or a 401 was seen).
    const handleExpired = useCallback(async () => {
        try {
            await ImpersonationService.end()
        } catch {
            // The end call may 401 too; that's fine, we still restore locally.
        }
        ImpersonationUtility.restoreAdmin(null)
        setSession(null)
        hardRedirect(ADMIN_LANDING)
    }, [])

    const contextData = useMemo(
        () => ({
            session,
            isImpersonating,
            starting,
            ending,
            currentUser: AuthService.getUser(),
            startImpersonation,
            endImpersonation,
            handleExpired,
        }),
        [
            session,
            isImpersonating,
            starting,
            ending,
            startImpersonation,
            endImpersonation,
            handleExpired,
        ],
    )

    return (
        <ImpersonationContext.Provider value={contextData}>
            {children}
        </ImpersonationContext.Provider>
    )
}

export const useImpersonation = () => useContext(ImpersonationContext)
